import { IDocumentResponse } from '@literax/models/document.model';

/** True when any participant in the collection is the viewer. A missing collection is `false`. */
const hasSelf = (participants?: { isOneSelf?: boolean }[]): boolean =>
  (participants ?? []).some((participant) => participant?.isOneSelf === true);

/**
 * True when the viewer is an observer on this document.
 *
 * Observers live in their own `signRequests.observers` collection and are absent from the signer
 * collections, so the participant object built from signers cannot answer this. The credential is
 * deliberately not part of the test: the role is what withholds the signature action, so a guest
 * observer and an observer with a session are both observers (D-18).
 */
export const isObserverOfDocument = (document?: IDocumentResponse): boolean =>
  hasSelf(document?.signRequests?.observers);

/**
 * True when the viewer signs this document themselves.
 *
 * The collections are the ones the workspace button bar resolves its own participant from:
 * legal persons, natural persons, and the substitutes who sign in place of a natural person. A
 * substitute counts, because they hold the signature button of the person they replace.
 */
const isSignerOfDocument = (document?: IDocumentResponse): boolean => {
  const naturalPersons = document?.signRequests?.naturalPersons ?? [];

  return (
    hasSelf(document?.signRequests?.legalPersons) ||
    hasSelf(naturalPersons) ||
    hasSelf(naturalPersons.map((person) => person.substitute))
  );
};

/**
 * True when the viewer observes this document and does not sign it.
 *
 * This is the test that withholds instructions written for signers. Nothing stops the same person
 * being both an observer and a signer on one document — no unique index, no validator — and such a
 * viewer does have a signature button to enable, so the signer role wins.
 */
export const isObserverOnlyOfDocument = (
  document?: IDocumentResponse
): boolean => isObserverOfDocument(document) && !isSignerOfDocument(document);

export const reindex = (
  group: any[],
  initialOrder: number,
  currentDragId: number
) => {
  let currentOrder = initialOrder;
  const requestIndexed = [];

  group.forEach(
    (el: any, index: number, array: any[]) => {
      const previousElement = requestIndexed[index - 1];
      const nextElement = array[index + 1];
      const differential = Math.abs(currentOrder - el.orderingTurn);
      const isBetweenGroup = currentOrder === nextElement?.orderingTurn;
      const isFirstElement = previousElement === undefined;
      const isLastElement = nextElement === undefined;
      const differentialNotIsZero = differential >= 1;

      el = { ...el };

      if (isFirstElement) {
        currentOrder = el.orderingTurn;
        el.orderingTurn = initialOrder;
      } else if (isLastElement && el.id === currentDragId) {
        el.orderingTurn = previousElement.orderingTurn + 1;
      } else if (el.orderingTurn === currentOrder) {
        currentOrder = el.orderingTurn;
        el.orderingTurn = previousElement.orderingTurn;
      } else if (
        differentialNotIsZero &&
        isBetweenGroup &&
        nextElement.id !== currentDragId
      ) {
        el.orderingTurn = previousElement.orderingTurn;
      } else if (differentialNotIsZero) {
        currentOrder = el.orderingTurn;
        el.orderingTurn = previousElement.orderingTurn + 1;
      }

      requestIndexed.push(el);
    }
  );
  return requestIndexed;
};

export const getOrderListBodyForSignRequest = (
  requestOrdered: any[]
) => {
  return requestOrdered.map((el: any) => ({
    id: el.signRequestId,
    order: el.orderingTurn
  }));
};

export const movePositionInArray = (
  array: any[],
  originalPosition: number,
  newPosition: number
) => {
  const tempArray = [...array];
  if (newPosition >= tempArray.length) {
    let k = newPosition - tempArray.length + 1;
    while (k--) {
      tempArray.push(undefined);
    }
  }
  tempArray.splice(newPosition, 0, tempArray.splice(originalPosition, 1)[0]);
  return tempArray.filter((el: any) => el !== undefined);
};
